import {
  BASE_URL,
  BASE_URL_GI_DOM_ABSOLUTE,
  BASE_URL_GI_INTL_ABSOLUTE,
  BASE_URL_INTL_ABSOLUTE,
  CCDE_SUPPORT,
  CURRENCY_CODES_FOR_COUNTRY,
  MMT_DOT_COM_CURRENCY,
  destinationPickerDTPageType,
  fareFinderDTPageType,
  flightIsm2PageType,
  flightsIsmDTNewPageType,
  flightsStaticPageType,
  flightStatusPageType,
  GI_BASE_URL,
  ismDestinationPickerPageType,
  ismFareFinderServicePageType,
  ismPageService2PageType,
  ismPageServicePageType,
  LANG,
  LANGUAGE,
  staticTemplatePageType,
  TOKEN_ROUTE_URL_PATH,
} from "./Constants";

const isMmtDotComDomain = (domain = "") => {
  const host = String(domain || "").toLowerCase().split(":")[0];
  return host.includes("makemytrip.com") && !host.includes("goibibo.com");
};

const SCHEMA_CONTEXT = "https://schema.org";

const LANG_CODE_MAP = {
  hin: "hi",
  ara: "ar",
  eng: "en",
};

const ORGANIZATION_CONFIG = {
  mmt: {
    name: "MakeMyTrip",
    url: "https://www.makemytrip.com",
    logo: "https://promos.makemytrip.com/Growth/Images/3x/mmt_dt_header_icon_3x.png",
  },
  gi: {
    name: "Goibibo",
    url: "https://www.goibibo.com",
    logo: "https://gos3.ibcdn.com/dweb2app-qr-logo-1664447597.png",
  },
};

const FARE_OFFER_MAPPINGS = [
  { key: "cheap0fare", name: "Today" },
  { key: "cheap7fare", name: "7 Days" },
  { key: "cheap_7_days_fare", name: "7 Days" },
  { key: "cheap30fare", name: "30 Days" },
  { key: "cheap_30_days_fare", name: "30 Days" },
  { key: "cheap90fare", name: "90 Days" },
  { key: "cheap365fare", name: "365 Days" },
  { key: "cheapest_fare", name: "Cheapest Fare" },
  { key: "minimum_fare", name: "Minimum Fare" },
  { key: "airline_cheapest_price", name: "Airline Cheapest Fare" },
];

const normalizeBaseUrl = (baseUrl = "") => {
  if (!baseUrl) return "https://www.makemytrip.com";
  return baseUrl.startsWith("//") ? `https:${baseUrl}` : baseUrl;
};

const resolveSchemaHostFromCountryFolder = (countryFolder = "") => {
  const countryPhrase = String(countryFolder || "").trim().toLowerCase();

  if (countryPhrase === "united_arab_emirates") {
    return "ae.makemytrip.global";
  }
  if (countryPhrase === "saudi_arabia") {
    return "sa.makemytrip.global";
  }
  if (!countryPhrase || countryPhrase === "india") {
    return "www.makemytrip.com";
  }

  return "www.makemytrip.global";
};

export const resolveSchemaHost = ({
  isGoibibo = false,
  currentDomainName = "",
  countryFolder = "",
  tokens = {},
} = {}) => {
  if (isGoibibo) {
    return "www.goibibo.com";
  }

  const domain = String(currentDomainName || "").toLowerCase();
  if (domain.includes("ae.makemytrip.global")) return "ae.makemytrip.global";
  if (domain.includes("sa.makemytrip.global")) return "sa.makemytrip.global";
  if (domain.includes("www.makemytrip.global")) return "www.makemytrip.global";
  if (domain.includes("makemytrip.global")) return "www.makemytrip.global";
  if (domain.includes("makemytrip.com")) return "www.makemytrip.com";

  const countryPhrase = countryFolder || tokens.country_url_phrase || "";

  return resolveSchemaHostFromCountryFolder(countryPhrase);
};

export const resolveSchemaOrganizationUrl = ({
  isGoibibo = false,
  currentDomainName = "",
  countryFolder = "",
  tokens = {},
} = {}) => {
  if (isGoibibo) {
    return ORGANIZATION_CONFIG.gi.url;
  }

  const host = resolveSchemaHost({
    isGoibibo,
    currentDomainName,
    countryFolder,
    tokens,
  });

  return `https://${host}`;
};

const toAbsoluteUrl = (url = "", baseUrl = "https://www.makemytrip.com") => {
  if (!url || url === "#") return undefined;

  const trimmedUrl = String(url).trim();

  if (/^https?:\/\//i.test(trimmedUrl)) {
    return trimmedUrl;
  }

  // MMT breadcrumb templates often use protocol-relative URLs like //www.makemytrip.com/
  if (trimmedUrl.startsWith("//")) {
    return `https:${trimmedUrl}`;
  }

  const normalizedBase = normalizeBaseUrl(baseUrl).replace(/\/$/, "");
  return trimmedUrl.startsWith("/") ? `${normalizedBase}${trimmedUrl}` : `${normalizedBase}/${trimmedUrl}`;
};

const parseFareValue = (fare) => {
  if (fare == null || fare === "") return null;
  const numericValue = parseFloat(String(fare).replace(/[^0-9.]/g, ""));
  return Number.isFinite(numericValue) && numericValue > 0 ? numericValue : null;
};

const resolveLanguageCode = (data = {}) => {
  const language = data[LANGUAGE] || data.language;
  if (language && language !== "en") {
    return language;
  }

  const lang = data[LANG] || data.lang;
  if (lang && LANG_CODE_MAP[lang]) {
    return LANG_CODE_MAP[lang];
  }

  return "";
};

const getResolvedTokens = (tokenJson = {}) => {
  const tokensList = tokenJson?.tokensList || {};
  const { tokensList: _tokensList, ...rootTokens } = tokenJson || {};

  return {
    ...tokensList,
    ...rootTokens,
  };
};

const resolveJsSettingJson = (data = {}) => {
  const rawSetting = data.jsSettingJson ?? data.jsSetting ?? data.pageData?.jsSettingJson;

  if (!rawSetting) return {};
  if (typeof rawSetting === "string") {
    try {
      return JSON.parse(rawSetting);
    } catch (error) {
      return {};
    }
  }

  return rawSetting;
};

const getBestFare = (tokens = {}) => {
  const fares = FARE_OFFER_MAPPINGS.map(({ key }) => parseFareValue(tokens[key])).filter(Boolean);

  if (fares.length === 0) return null;
  return Math.min(...fares);
};

const getProductName = ({ tokens = {}, isIntl = false, title = "" } = {}) => {
  if (title) return title;

  const srcCityName = tokens.src_city_name || tokens.city_name;
  const destCityName = tokens.dest_city_name;

  if (srcCityName && destCityName) {
    return isIntl
      ? `${srcCityName} to ${destCityName} International Flights`
      : `${srcCityName} to ${destCityName} Flights`;
  }

  if (destCityName) return `Flights to ${destCityName}`;
  if (srcCityName) return `Flights from ${srcCityName}`;
  return "";
};

const buildPageUrl = ({
  currentUrl = "",
  routePath = "",
  currentDomainName = "",
  schemaBaseUrl = "",
} = {}) => {
  if (currentUrl) return currentUrl;

  if (routePath) {
    const normalizedRoutePath = routePath.startsWith("/") ? routePath : `/${routePath}`;
    if (currentDomainName) {
      return `https://${currentDomainName}${normalizedRoutePath}`;
    }
    return toAbsoluteUrl(normalizedRoutePath, schemaBaseUrl) || schemaBaseUrl;
  }

  return schemaBaseUrl;
};

export const normalizeSchemaPageData = (data = {}) => {
  const tokenJson = data.tokenJson || data.pageData?.tokenJson || {};
  const tokens = getResolvedTokens(tokenJson);
  const countryFolder =
    data.country_folder ||
    data.pageData?.country_folder ||
    tokens.country_url_phrase ||
    "";

  return {
    ...data,
    ...(data.pageData || {}),
    isGoibibo: Boolean(data.isGoibibo ?? data.pageData?.isGoibibo),
    isIntl: Boolean(data.isIntl ?? data.pageData?.isIntl),
    isDeviceMobile: Boolean(data.isDeviceMobile ?? data.pageData?.isDeviceMobile),
    country_folder: countryFolder,
    currentDomainName: data.currentDomainName || data.pageData?.currentDomainName || "",
    language: resolveLanguageCode(data),
    tokenJson: { ...tokenJson, tokensList: tokens },
    jsSettingJson: resolveJsSettingJson(data),
  };
};

export const resolveSchemaBaseUrl = ({
  isGoibibo = false,
  isIntl = false,
  countryFolder = "",
  language = "",
  currentDomainName = "",
  tokens = {},
} = {}) => {
  const baseHost = isGoibibo
    ? normalizeBaseUrl(GI_BASE_URL)
    : resolveSchemaOrganizationUrl({
        isGoibibo,
        currentDomainName,
        countryFolder,
        tokens,
      });
  const normalizedCountryFolder = String(countryFolder || "").trim().toLowerCase();
  const countrySegment =
    normalizedCountryFolder && normalizedCountryFolder !== "india" ? `${normalizedCountryFolder}/` : "";
  const languageSegment = language ? `${language}/` : "";
  const lob = isIntl ? "international-flights/" : "flights/";

  if (isGoibibo) {
    return `${baseHost}/${lob}${countrySegment}${languageSegment}`;
  }

  if (isIntl) {
    return `${baseHost}/international-flights/${countrySegment}${languageSegment}`;
  }

  return `${baseHost}/flights/${languageSegment}`;
};

export const resolveSchemaContext = (data = {}) => {
  const normalizedData = normalizeSchemaPageData(data);
  const {
    isGoibibo = false,
    isIntl = false,
    currentUrl = "",
    currentDomainName = "",
    country_folder: countryFolder = "",
    ccde = "IN",
    language = "",
    localeSettings = {},
    tokenJson = {},
    pageData = {},
  } = normalizedData;

  const tokens = getResolvedTokens(tokenJson);
  const routePath =
    pageData?.[TOKEN_ROUTE_URL_PATH] ||
    normalizedData[TOKEN_ROUTE_URL_PATH] ||
    tokens[TOKEN_ROUTE_URL_PATH] ||
    tokens.url ||
    "";

  const schemaBaseUrl = resolveSchemaBaseUrl({
    isGoibibo,
    isIntl,
    countryFolder,
    language,
    currentDomainName,
    tokens,
  });

  const pageUrl = buildPageUrl({
    currentUrl,
    routePath,
    currentDomainName,
    schemaBaseUrl,
  });

  const priceCurrency = resolvePriceCurrency({
    localeSettings,
    tokens,
    ccde,
    countryFolder,
    currentDomainName,
  });

  return {
    isGoibibo,
    isIntl,
    countryFolder,
    language,
    schemaBaseUrl,
    pageUrl,
    priceCurrency,
    tokens,
  };
};

export const resolvePriceCurrency = ({
  localeSettings = {},
  tokens = {},
  ccde = "IN",
  countryFolder = "",
  currentDomainName = "",
} = {}) => {
  if (isMmtDotComDomain(currentDomainName)) {
    return MMT_DOT_COM_CURRENCY;
  }

  if (localeSettings?.userCurrency) {
    return localeSettings.userCurrency;
  }

  if (tokens.country_folder_code && CURRENCY_CODES_FOR_COUNTRY[tokens.country_folder_code]) {
    return CURRENCY_CODES_FOR_COUNTRY[tokens.country_folder_code];
  }

  const normalizedCountryFolder = String(countryFolder || tokens.country_url_phrase || "").toLowerCase();
  if (normalizedCountryFolder && CCDE_SUPPORT[normalizedCountryFolder]) {
    const mappedCountryCode = CCDE_SUPPORT[normalizedCountryFolder];
    return CURRENCY_CODES_FOR_COUNTRY[mappedCountryCode] || "USD";
  }

  const rawCurrency = tokens.currency || tokens.country_folder_code;
  if (rawCurrency) {
    const normalizedCurrency = String(rawCurrency).replace(/[^A-Za-z]/g, "").toUpperCase();
    if (normalizedCurrency.length === 3) {
      return normalizedCurrency;
    }
  }

  if (ccde === "AE") return "AED";
  if (ccde === "US") return "USD";
  return "INR";
};

const getAirportIataCode = (tokens = {}, type = "src") => {
  if (type === "src") {
    return (
      tokens.source_city_airport_code ||
      tokens.src_city_airport_code ||
      tokens.src_city_code ||
      tokens.city_code ||
      ""
    );
  }

  return (
    tokens.destination_city_airport_code ||
    tokens.dest_city_airport_code ||
    tokens.dest_city_code ||
    ""
  );
};

const getAirportName = (tokens = {}, type = "src") => {
  if (type === "src") {
    return (
      tokens.src_airport_name ||
      tokens.src_city_airport_name ||
      tokens.airport_name ||
      tokens.src_city_name ||
      tokens.city_name ||
      ""
    );
  }

  return (
    tokens.dest_airport_name ||
    tokens.dest_city_airport_name ||
    tokens.dest_city_name ||
    ""
  );
};

const getAirportCountryCode = (tokens = {}, type = "src") => {
  if (type === "src") {
    return tokens.src_country_code || tokens.country_code || "";
  }
  return tokens.dest_country_code || "";
};

export const generateOrganizationSchema = ({
  isGoibibo = false,
  currentDomainName = "",
  countryFolder = "",
  tokens = {},
} = {}) => {
  const orgConfig = isGoibibo ? ORGANIZATION_CONFIG.gi : ORGANIZATION_CONFIG.mmt;
  const orgUrl = resolveSchemaOrganizationUrl({
    isGoibibo,
    currentDomainName,
    countryFolder,
    tokens,
  });

  return {
    "@type": "Organization",
    "@id": `${orgUrl}/#organization`,
    name: orgConfig.name,
    url: orgUrl,
    logo: orgConfig.logo,
  };
};

export const generateBreadcrumbSchema = ({ breadcrumb = [], baseUrl = BASE_URL, currentUrl = "" } = {}) => {
  if (!Array.isArray(breadcrumb) || breadcrumb.length === 0) return null;

  const normalizedBaseUrl = normalizeBaseUrl(baseUrl);
  const itemListElement = breadcrumb
    .map(({ title, url }, index) => {
      const name = title?.trim();
      if (!name) return null;

      let itemUrl = url ? toAbsoluteUrl(url, normalizedBaseUrl) : undefined;
      const isLastItem = index === breadcrumb.length - 1;
      if (!itemUrl && isLastItem && currentUrl) {
        itemUrl = toAbsoluteUrl(currentUrl, normalizedBaseUrl) || currentUrl;
      }

      const listItem = {
        "@type": "ListItem",
        position: index + 1,
        name,
      };

      if (itemUrl) {
        listItem.item = itemUrl;
      }

      return listItem;
    })
    .filter(Boolean);

  if (itemListElement.length === 0) return null;

  return {
    "@type": "BreadcrumbList",
    itemListElement,
  };
};

export const generateAirportSchema = ({ name = "", iataCode = "", addressCountry = "" } = {}) => {
  const normalizedIataCode = String(iataCode || "").trim().toUpperCase();
  if (!normalizedIataCode) return null;

  const airport = {
    "@type": "Airport",
    iataCode: normalizedIataCode,
  };

  if (name) {
    airport.name = name;
  }

  if (addressCountry) {
    airport.address = {
      "@type": "PostalAddress",
      addressCountry,
    };
  }

  return airport;
};

export const generateFlightSchema = ({
  tokens = {},
  provider = null,
  pageUrl = "",
  isIntl = false,
} = {}) => {
  const departureAirport = generateAirportSchema({
    name: getAirportName(tokens, "src"),
    iataCode: getAirportIataCode(tokens, "src"),
    addressCountry: getAirportCountryCode(tokens, "src"),
  });
  const arrivalAirport = generateAirportSchema({
    name: getAirportName(tokens, "dest"),
    iataCode: getAirportIataCode(tokens, "dest"),
    addressCountry: getAirportCountryCode(tokens, "dest"),
  });

  if (!departureAirport || !arrivalAirport) return null;

  const flight = {
    "@type": "Flight",
    departureAirport,
    arrivalAirport,
  };

  const airlineName = tokens.cheap0airline_name || tokens.airline_name || tokens.minimum_fare_flight_operator;
  const airlineCode = tokens.airline_code || tokens.cheap0airline_number;

  if (airlineName || airlineCode) {
    flight.provider = {
      "@type": "Airline",
      ...(airlineName ? { name: airlineName } : {}),
      ...(airlineCode ? { iataCode: String(airlineCode).toUpperCase() } : {}),
    };
  } else if (provider) {
    flight.provider = provider;
  }

  if (pageUrl) {
    flight.url = pageUrl;
  }

  const srcCityName = tokens.src_city_name || tokens.city_name;
  const destCityName = tokens.dest_city_name;
  const flightName = [srcCityName, destCityName].filter(Boolean).join(" to ");
  if (flightName) {
    flight.name = isIntl ? `${flightName} International Flight` : `${flightName} Flight`;
  }

  return flight;
};

export const generateFlightReservationSchema = ({
  flightSchema = null,
  tokens = {},
  provider = null,
  pageUrl = "",
  priceCurrency = "INR",
} = {}) => {
  if (!flightSchema) return null;

  const totalPrice = getBestFare(tokens);
  const reservation = {
    "@type": "FlightReservation",
    reservationStatus: "https://schema.org/ReservationHold",
    reservationFor: flightSchema,
  };

  if (totalPrice) {
    reservation.totalPrice = totalPrice;
    reservation.priceCurrency = priceCurrency;
  }

  if (provider) {
    reservation.provider = provider;
    reservation.bookingAgent = provider;
  }

  if (pageUrl) {
    reservation.url = pageUrl;
  }

  return reservation;
};

export const generateOfferSchema = ({
  price = null,
  priceCurrency = "INR",
  pageUrl = "",
  name = "",
  seller = null,
} = {}) => {
  if (!price) return null;

  const offer = {
    "@type": "Offer",
    price,
    priceCurrency,
    availability: "https://schema.org/InStock",
  };

  if (pageUrl) {
    offer.url = pageUrl;
  }

  if (name) {
    offer.name = name;
  }

  if (seller) {
    offer.seller = seller;
  }

  return offer;
};

export const generateAggregateOfferSchema = ({
  tokens = {},
  priceCurrency = "INR",
  pageUrl = "",
  seller = null,
} = {}) => {
  const seenPrices = new Set();
  const offers = [];

  FARE_OFFER_MAPPINGS.forEach(({ key, name }) => {
    const price = parseFareValue(tokens[key]);
    const dedupeKey = `${name}:${price}`;
    if (!price || seenPrices.has(dedupeKey)) return;

    seenPrices.add(dedupeKey);
    const offer = generateOfferSchema({
      price,
      priceCurrency,
      pageUrl,
      name,
      seller,
    });
    if (offer) offers.push(offer);
  });

  if (offers.length === 0) return null;

  const prices = offers.map((offer) => offer.price);
  const aggregateOffer = {
    "@type": "AggregateOffer",
    priceCurrency,
    lowPrice: Math.min(...prices),
    offerCount: offers.length,
    offers,
  };

  if (pageUrl) {
    aggregateOffer.url = pageUrl;
  }

  if (seller) {
    aggregateOffer.seller = seller;
  }

  return aggregateOffer;
};

export const generateProductSchema = ({
  tokens = {},
  isIntl = false,
  title = "",
  description = "",
  pageUrl = "",
  brand = null,
  seller = null,
  priceCurrency = "INR",
} = {}) => {
  const name = getProductName({ tokens, isIntl, title });
  if (!name) return null;

  const aggregateOffer = generateAggregateOfferSchema({
    tokens,
    priceCurrency,
    pageUrl,
    seller,
  });

  const product = {
    "@type": "Product",
    name,
  };

  if (description) {
    product.description = description;
  }

  if (pageUrl) {
    product.url = pageUrl;
  }

  if (brand) {
    product.brand = {
      "@type": "Brand",
      name: brand,
    };
  }

  if (aggregateOffer) {
    product.offers = aggregateOffer;
  }

  return product;
};

export const generateSeoPageSchemas = (data = {}) => {
  const normalizedData = normalizeSchemaPageData(data);
  const { jsSettingJson = {}, title = "", description = "" } = normalizedData;
  const {
    isGoibibo,
    isIntl,
    schemaBaseUrl,
    pageUrl,
    priceCurrency,
    tokens,
  } = resolveSchemaContext(normalizedData);

  const graph = [];
  const organizationSchema = generateOrganizationSchema({
    isGoibibo,
    currentDomainName: normalizedData.currentDomainName,
    countryFolder: normalizedData.country_folder,
    tokens,
  });
  const seller = { "@id": organizationSchema["@id"] };
  graph.push(organizationSchema);

  const breadcrumbSchema = generateBreadcrumbSchema({
    breadcrumb: jsSettingJson?.breadcrumb,
    baseUrl: schemaBaseUrl,
    currentUrl: pageUrl,
  });
  if (breadcrumbSchema) {
    graph.push(breadcrumbSchema);
  }

  const productSchema = generateProductSchema({
    tokens,
    isIntl,
    title,
    description,
    pageUrl,
    brand: organizationSchema.name,
    seller,
    priceCurrency,
  });

  if (productSchema) {
    graph.push(productSchema);
  }

  const flightSchema = generateFlightSchema({
    tokens,
    provider: seller,
    pageUrl,
    isIntl,
  });

  if (flightSchema) {
    graph.push(flightSchema);

    const flightReservationSchema = generateFlightReservationSchema({
      flightSchema,
      tokens,
      provider: seller,
      pageUrl,
      priceCurrency,
    });

    if (flightReservationSchema) {
      graph.push(flightReservationSchema);
    }
  } else {
    const srcAirportSchema = generateAirportSchema({
      name: getAirportName(tokens, "src"),
      iataCode: getAirportIataCode(tokens, "src"),
      addressCountry: getAirportCountryCode(tokens, "src"),
    });
    const destAirportSchema = generateAirportSchema({
      name: getAirportName(tokens, "dest"),
      iataCode: getAirportIataCode(tokens, "dest"),
      addressCountry: getAirportCountryCode(tokens, "dest"),
    });

    if (srcAirportSchema) {
      graph.push(srcAirportSchema);
    }
    if (destAirportSchema && destAirportSchema.iataCode !== srcAirportSchema?.iataCode) {
      graph.push(destAirportSchema);
    }
  }

  if (graph.length === 0) return null;

  return {
    "@context": SCHEMA_CONTEXT,
    "@graph": graph,
  };
};

export const getSeoSchemaScriptContent = (data = {}) => {
  const schema = generateSeoPageSchemas(data);
  if (!schema) return null;
  return JSON.stringify(schema);
};

export const shouldIncludeSeoSchema = (pageType = "") => {
  if (!pageType || pageType === "offerPage") return false;

  return Boolean(
    flightsIsmDTNewPageType[pageType] ||
      ismPageServicePageType[pageType] ||
      ismPageService2PageType[pageType] ||
      ismFareFinderServicePageType[pageType] ||
      ismDestinationPickerPageType[pageType] ||
      flightsStaticPageType[pageType] ||
      fareFinderDTPageType[pageType] ||
      flightStatusPageType[pageType] ||
      flightIsm2PageType[pageType] ||
      destinationPickerDTPageType[pageType] ||
      staticTemplatePageType[pageType],
  );
};
